import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from 'react';
import { useEffect, useId, useRef } from 'react';

export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

/* ---------------------------------------------------------------- Button */

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
type ButtonSize = 'sm' | 'md';

const buttonVariants: Record<ButtonVariant, string> = {
  primary:
    'bg-ember-500 text-white hover:bg-ember-400 focus-visible:bg-ember-400 border border-ember-600',
  secondary:
    'bg-surface-3 text-ink hover:bg-line border border-line-strong',
  ghost:
    'bg-transparent text-ink-dim hover:text-ink hover:bg-surface-2 border border-transparent',
  danger:
    'bg-transparent text-bad hover:bg-bad hover:text-white border border-bad/60',
};

const buttonSizes: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-xs',
  md: 'h-10 px-4 text-sm',
};

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
};

export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  className,
  disabled,
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cx(
        'inline-flex items-center justify-center gap-2 rounded-md font-medium transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-50',
        buttonVariants[variant],
        buttonSizes[size],
        className,
      )}
    >
      {loading && <Spinner className="size-3.5" />}
      {children}
    </button>
  );
}

export function Spinner({ className }: { className?: string }) {
  return (
    <span
      role="status"
      aria-label="Loading"
      className={cx(
        'inline-block animate-spin rounded-full border-2 border-current border-t-transparent',
        className ?? 'size-4',
      )}
    />
  );
}

/* ------------------------------------------------------------------ Card */

export function Card({
  title,
  description,
  actions,
  children,
  className,
}: {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={cx(
        'rounded-xl border border-line bg-surface-1/80 shadow-lg shadow-black/30 backdrop-blur',
        className,
      )}
    >
      {(title || actions) && (
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-4 py-3 sm:px-5">
          <div className="min-w-0">
            {title && <h2 className="truncate text-base font-semibold text-ink">{title}</h2>}
            {description && <p className="mt-0.5 text-xs text-ink-dim">{description}</p>}
          </div>
          {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
        </header>
      )}
      <div className="px-4 py-4 sm:px-5">{children}</div>
    </section>
  );
}

/* ----------------------------------------------------------------- Badge */

type BadgeTone = 'neutral' | 'ok' | 'warn' | 'bad' | 'info' | 'gold';

const badgeTones: Record<BadgeTone, string> = {
  neutral: 'bg-surface-3 text-ink-dim border-line-strong',
  ok: 'bg-ok/15 text-ok border-ok/40',
  warn: 'bg-warn/15 text-warn border-warn/40',
  bad: 'bg-bad/15 text-bad border-bad/40',
  info: 'bg-info/15 text-info border-info/40',
  gold: 'bg-gold-400/15 text-gold-300 border-gold-400/40',
};

export function Badge({ tone = 'neutral', children }: { tone?: BadgeTone; children: ReactNode }) {
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium',
        badgeTones[tone],
      )}
    >
      {children}
    </span>
  );
}

/** Maps a bot status to a tone so the same colour means the same thing everywhere. */
export function toneForBotStatus(status: string): BadgeTone {
  switch (status) {
    case 'connected':
      return 'ok';
    case 'busy':
      return 'info';
    case 'connecting':
      return 'warn';
    case 'error':
      return 'bad';
    default:
      return 'neutral';
  }
}

/* ----------------------------------------------------------------- Field */

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: (props: { id: string; describedBy?: string }) => ReactNode;
}) {
  const id = useId();
  const describedBy = hint || error ? `${id}-desc` : undefined;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-xs font-medium tracking-wide text-ink-dim uppercase">
        {label}
      </label>
      {children({ id, describedBy })}
      {(hint || error) && (
        <p id={describedBy} className={cx('text-xs', error ? 'text-bad' : 'text-ink-faint')}>
          {error ?? hint}
        </p>
      )}
    </div>
  );
}

const controlBase =
  'rounded-md border border-line-strong bg-surface-2 text-ink ' +
  'placeholder:text-ink-faint focus:border-info focus:outline-none disabled:opacity-50';
const controlClasses = cx(controlBase, 'w-full px-3 py-2 text-sm');
/** Toolbar-sized variant: fits next to small buttons without fighting the defaults. */
const compactControlClasses = cx(controlBase, 'h-8 px-2 py-0 text-xs');

export function TextInput({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...rest} className={cx(controlClasses, className)} />;
}

export function Select({
  className,
  compact = false,
  children,
  ...rest
}: SelectHTMLAttributes<HTMLSelectElement> & { compact?: boolean }) {
  return (
    <select
      {...rest}
      className={cx(compact ? compactControlClasses : controlClasses, 'appearance-none', className)}
    >
      {children}
    </select>
  );
}

export function Checkbox({
  label,
  className,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { label: string }) {
  return (
    <label className="flex cursor-pointer items-center gap-2 text-sm text-ink">
      <input
        {...rest}
        type="checkbox"
        className={cx('size-4 rounded border-line-strong bg-surface-2 accent-ember-500', className)}
      />
      {label}
    </label>
  );
}

/* ---------------------------------------------------------------- Dialog */

const FOCUSABLE_SELECTOR = [
  'input:not([type="hidden"]):not([disabled])',
  'textarea:not([disabled])',
  'select:not([disabled])',
  'button:not([disabled])',
  'a[href]',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
}) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  // Callers pass a fresh arrow function on every render, so the effects below
  // must not depend on it: they would re-run whenever anything re-rendered the
  // page, including a background poll, and steal the caret out of a field.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCloseRef.current();
    };
    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  // Focus moves once, on opening: to the first field so typing starts straight
  // away, or to the panel when the dialog only asks for a confirmation. What
  // had focus before gets it back when the dialog closes.
  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement as HTMLElement | null;
    const firstField = bodyRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
    (firstField ?? panelRef.current)?.focus({ preventScroll: true });
    return () => {
      if (opener?.isConnected) opener.focus({ preventScroll: true });
    };
  }, [open]);

  if (!open) return null;

  const widths = { sm: 'max-w-md', md: 'max-w-2xl', lg: 'max-w-4xl' } as const;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4 sm:p-8">
      <div className="absolute inset-0" onClick={onClose} aria-hidden="true" />
      <div
        ref={panelRef}
        role="dialog"
        onKeyDown={(event) => {
          if (event.key !== 'Tab') return;
          const panel=panelRef.current;
          if (!panel) return;
          const controls=Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(element=>!element.matches(':disabled')&&element.getAttribute('aria-hidden')!=='true');
          const first=controls[0];const last=controls.at(-1);
          if (!first||!last) {event.preventDefault();panel.focus();return;}
          if (event.shiftKey&&(document.activeElement===first||document.activeElement===panel)) {event.preventDefault();last.focus();}
          else if (!event.shiftKey&&(document.activeElement===last||document.activeElement===panel)) {event.preventDefault();first.focus();}
        }}
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className={cx(
          'relative w-full rounded-xl border border-line bg-surface-1 shadow-2xl outline-none',
          widths[size],
        )}
      >
        <header className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
          <div>
            <h2 className="text-lg font-semibold text-ink">{title}</h2>
            {description && <p className="mt-1 text-sm text-ink-dim">{description}</p>}
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close dialog">
            ✕
          </Button>
        </header>
        <div ref={bodyRef} className="max-h-[70vh] overflow-y-auto px-5 py-4 scrollbar-slim">
          {children}
        </div>
        {footer && (
          <footer className="flex flex-wrap justify-end gap-2 border-t border-line px-5 py-4">
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------- Table */

export function TableShell({ children }: { children: ReactNode }) {
  return (
    <div className="overflow-x-auto scrollbar-slim">
      <table className="w-full min-w-[36rem] text-left text-sm">{children}</table>
    </div>
  );
}

export function Th({ children, className }: { children?: ReactNode; className?: string }) {
  return (
    <th
      className={cx(
        'border-b border-line px-3 py-2 text-xs font-semibold tracking-wide text-ink-dim uppercase',
        className,
      )}
    >
      {children}
    </th>
  );
}

export function Td({ children, className }: { children?: ReactNode; className?: string }) {
  return <td className={cx('border-b border-line/60 px-3 py-2 align-middle', className)}>{children}</td>;
}

/* --------------------------------------------------------- State display */

export function StateBlock({
  tone = 'neutral',
  title,
  detail,
  action,
}: {
  tone?: 'neutral' | 'bad';
  title: string;
  detail?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div
      className={cx(
        'flex flex-col items-center gap-2 rounded-lg border border-dashed px-4 py-10 text-center',
        tone === 'bad' ? 'border-bad/40 text-bad' : 'border-line text-ink-dim',
      )}
    >
      <p className="text-sm font-medium">{title}</p>
      {detail && <div className="max-w-prose text-xs text-ink-faint">{detail}</div>}
      {action}
    </div>
  );
}

export function LoadingBlock({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-3 px-4 py-10 text-sm text-ink-dim">
      <Spinner />
      {label}
    </div>
  );
}
